import React, { useState } from 'react';
import useDidUpdateEffect from '@use-effect/did-update';
import { useRouter } from 'next/router';
import { Chevron } from '@solent-university/solent-icons';

import {
    PaginationWrapper,
    PageLinksWrapper,
    StyledLink,
    StyledLinkIcon,
    CurrentPageLink,
    Ellipsis,
    StyledIcon
} from './Pagination.styled';

export interface Props {
    id?: string;
    initialPageIndex?: number;
    totalCount: number;
    pageSize: number;
    className?: string;
    fixedPageUrls?: string[];
}

const BUTTONS_TO_SHOW = 7;

const Pagination: React.FC<Props> = props => {
    const { id, initialPageIndex, totalCount, pageSize, className, fixedPageUrls } = props;

    const router = useRouter();

    // Add 1 to initialPageIndex if set as index is 1 less than currentPage
    const [currentPage, setCurrentPage] = useState(initialPageIndex ? initialPageIndex + 1 : 1);
    const totalPages = Math.ceil(totalCount / pageSize);

    useDidUpdateEffect(() => {
        setCurrentPage(initialPageIndex ? initialPageIndex + 1 : 1);
    }, [initialPageIndex]);

    const getButtonsToShow = () => {
        const halfOfToShow = Math.ceil(BUTTONS_TO_SHOW / 2);
        const pageButtons: (number | null)[] = [];

        // If a value is null, an ellipsis will be rendered

        // Return an array with how many total pages there are
        if (totalPages <= BUTTONS_TO_SHOW) {
            return Array(totalPages)
                .fill(null)
                .map((_, i) => i + 1);
        }

        if (currentPage > halfOfToShow) {
            // Add the first page and break
            pageButtons.push(1, null);

            if (currentPage <= totalPages - halfOfToShow) {
                // Add halfOfToShow - 1 pages inbetween the first and last page
                for (let index = 0; index < halfOfToShow - 1; index++) {
                    pageButtons.push(currentPage + (index - Math.floor((halfOfToShow - 1) / 2)));
                }
                // Add last page
                pageButtons.push(null, totalPages);
            } else {
                // Add halfOfToShow + 1 pages at the end
                for (let index = 0; index < halfOfToShow + 1; index++) {
                    pageButtons.push(totalPages - (halfOfToShow - index));
                }
            }
        } else {
            // Add half of to show pages at the start
            pageButtons.push(
                ...Array(halfOfToShow + 1)
                    .fill(null)
                    .map((_, i) => i + 1)
            );

            // Add the last page
            pageButtons.push(null, totalPages);
        }

        return pageButtons;
    };

    return (
        <PaginationWrapper id={id} className={`c-pagination ${className}`}>
            <PageLinksWrapper>
                {currentPage !== 1 && (
                    <StyledLinkIcon
                        aria-label="Previous page"
                        className="c-pagination-link"
                        id={`${id}-pagination-previous`}
                        href={
                            fixedPageUrls
                                ? fixedPageUrls[currentPage - 2]
                                : {
                                      pathname: router.pathname,
                                      query: { ...router.query, page: currentPage - 1 }
                                  }
                        }
                    >
                        <StyledIcon icon={Chevron} $prev />
                        Prev
                    </StyledLinkIcon>
                )}

                {getButtonsToShow().map((pageNumber, index) => (
                    <React.Fragment key={`key-${pageNumber || index + totalPages}`}>
                        {pageNumber === null ? (
                            <Ellipsis>...</Ellipsis>
                        ) : pageNumber === currentPage ? (
                            <CurrentPageLink key={`pagination-page-${pageNumber}`}>
                                {currentPage}
                            </CurrentPageLink>
                        ) : (
                            <StyledLink
                                aria-label={`Page ${pageNumber}`}
                                className="c-pagination-link"
                                id={`${id}-page-${pageNumber}`}
                                href={
                                    fixedPageUrls
                                        ? fixedPageUrls[pageNumber - 1]
                                        : {
                                              pathname: router.pathname,
                                              query: { ...router.query, page: pageNumber }
                                          }
                                }
                            >
                                {pageNumber}
                            </StyledLink>
                        )}
                    </React.Fragment>
                ))}

                {currentPage !== totalPages && (
                    <StyledLinkIcon
                        aria-label="Next page"
                        className="c-pagination-link"
                        id={`${id}-pagination-next`}
                        href={
                            fixedPageUrls
                                ? fixedPageUrls[currentPage]
                                : {
                                      pathname: router.pathname,
                                      query: { ...router.query, page: currentPage + 1 }
                                  }
                        }
                    >
                        Next
                        <StyledIcon icon={Chevron} />
                    </StyledLinkIcon>
                )}
            </PageLinksWrapper>
        </PaginationWrapper>
    );
};

export default Pagination;
